import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BsDatepickerConfig, DatepickerDateCustomClasses, BsDatepickerDirective } from 'webcommon/ap-ngx-bootstrap/datepicker';
import { BsDatepickerValue } from 'webcommon/shared';
import { ApBaseComponent } from '../ap-base-component/index';

@Component({
  selector: 'ap-datepicker-next-prev',
  styleUrls: ['./ap-datepicker-next-prev.component.less'],
  templateUrl: './ap-datepicker-next-prev.component.html',
})
export class ApDatePickerNextPrevComponent extends ApBaseComponent implements OnInit, OnChanges {
  @Input() public disabled: boolean;
  @Input() public baseId: string;
  @Input() labelName: string;
  @Input() isShowLabel = true;
  @Input() selectedDate: Date = new Date();
  @Input() maxDate: Date;
  @Input() minDate: Date;
  @Output() selectedChanged: EventEmitter<Date | null> = new EventEmitter(true);
  @ViewChild('calendarDatePicker') calendarDatePicker: BsDatepickerDirective;

  constructor() {
    super();
  }

  calendarDateError: string | null = null;
  calendarDateFormatNoTime = 'yyyy mm dd';
  colorTheme = 'theme-emds-datepicker';
  dayOfWeek = '';
  Id = '';
  inValidCalendarDate = false;
  selectedCalendarDate: BsDatepickerValue = undefined;

  bsConfig: Partial<BsDatepickerConfig>;
  dateCustomClasses: DatepickerDateCustomClasses[];

  daysOfWeekText: string[] = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
  maxAllowedDate = new Date('2099-12-31');
  minAllowedDate = new Date('1900-01-01');

  public ngOnInit(): void {
    this.setId();
    this.setCalendarDates();
    const now = new Date();
    this.calendarDateError = 'Invalid Date';

    this.dateCustomClasses = [{ date: now, classes: ['emds-datepicker-current-date-background-color'] }];

    this.bsConfig = Object.assign(
      { containerClass: 'theme-emds-datepicker' },
      { adaptivePosition: true },
      { dateInputFormat: 'M/D/YYYY' },
      { showWeekNumbers: false },
      { selectFromOtherMonth: true },
      { showTodayButton: true }
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedDate) {
      this.setCalendarDates();
    }
  }

  public hasCalendarDateError(): boolean {
    return this.inValidCalendarDate;
  }

  public isDisabled(): boolean {
    return this.disabled;
  }

  public onCalendarDateChange(value: BsDatepickerValue): void {
    this.setCalendarDateError(false);
    if (value && this.isValidCalendarDate(value)) {
      this.setTimeToNoon(value);    // Today button uses current time of current date
      // If already set, ignore change
      if (this.selectedCalendarDate?.toISOString() === value.toISOString()) return;
      this.setSelectedCalendarDate(value);
    } else {
      this.dayOfWeek = '';
      this.setCalendarDateError(true);
      this.selectedChanged.emit(null);
    }
  }

  // Try to force the calendar to stay open when the Today button is clicked for quick navigation back to the current date.
  // Note: The Today button handler will be called after the bsValueChange handler.
  public attachTodayButtonHandler(): void {
    const todayDiv = document.getElementsByClassName('btn-today-wrapper');
    if (!todayDiv.length) return;
    const todayButton = todayDiv[0].getElementsByTagName('button');
    if (!todayButton.length) return;
    todayButton[0].addEventListener('click', this.onCalendarDatepickerTodayButtonClicked.bind(this));
  }

  private onCalendarDatepickerTodayButtonClicked(): void {
    this.calendarDatePicker.isOpen = true;
  }

  public setNextDay(): void {
    if (this.isDisabled() || !this.selectedCalendarDate) {
      return;
    }
    const newDate = this.addDays(this.selectedCalendarDate, 1);
    this.setSelectedCalendarDate(newDate);
  }

  public setPrevDay(): void {
    if (this.isDisabled() || !this.selectedCalendarDate) {
      return;
    }
    const newDate = this.addDays(this.selectedCalendarDate, -1);
    this.setSelectedCalendarDate(newDate);
  }

  private getDayOfWeek(date: Date): string {
    const d = date.getDay();
    return this.daysOfWeekText[d];
  }

  private setSelectedCalendarDate(newDate: Date): void {
    this.selectedCalendarDate = newDate;
    this.dayOfWeek = this.getDayOfWeek(newDate);
    this.selectedChanged.emit(newDate);
  }

  private addDays(date: Date, days: number): Date {
    // set hour to noon to work around issues with Javascript Date and Daylight Saving Time
    const d = new Date(date.setHours(12, 0, 0));
    d.setDate(d.getDate() + days);
    return d;
  }

  private isValidCalendarDate(calendarDate: Date): boolean {
    if (calendarDate.toString() === 'Invalid Date') {
      return false;
    }
    return calendarDate != null && calendarDate >= this.minAllowedDate && calendarDate <= this.maxAllowedDate;
  }

  private setCalendarDateError(isError: boolean): void {
    this.inValidCalendarDate = isError;
  }

  private setCalendarDates(): void {
    const today = this.selectedDate;
    // set hour to noon to work around issues with Javascript Date and Daylight Saving Time
    this.setTimeToNoon(today);
    this.selectedCalendarDate = today;
    this.dayOfWeek = this.getDayOfWeek(this.selectedCalendarDate);
  }

  private setTimeToNoon(date: Date) {
    date.setHours(12);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
  }

  private setId(): void {
    this.Id = this.baseId + '-datepicker-next-prev';
  }
}
